// Mirrors `BODYSHOP_DEFAULT_METADATA` in etx/server/src/.../team.entity.ts.
//
// We only mirror the numbers — the FE uses them to show a `placeholder $72`
// chip beside each input in Settings → Fees so the user can tell what's a
// real shop rate vs an unconfigured stand-in.
//
// IMPORTANT: don't use these numbers as the source of truth for "are fees
// configured?". The backend will eventually stop seeding defaults so that
// "any laborRate value > 0 == reviewed" — see `feesNotReviewed` below.

export const BODYSHOP_PLACEHOLDER_METADATA = {
  laborRate: {
    bodyLabor: 72,
    paintLabor: 70,
    frameLabor: 95,
    mechanical: 175,
    aluminumBody: 140,
    aluminumFrame: 155,
  },
  materials: {
    bodyMaterials: 9,
    paintMaterials: 48,
  },
  extraFees: {
    insideStorage: 85,
    outsideStorage: 55,
  },
  vehicleScans: {
    preRepairScan: 120,
    postRepairScan: 120,
  },
} as const

export type LaborField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.laborRate
export type MaterialsField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.materials
export type ExtraFeesField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.extraFees
export type VehicleScansField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.vehicleScans

// Single signal for "this body shop hasn't reviewed their fees yet."
// Used by useSetupTasks (warning) and useEstimatePrerequisites (gate).
//
// Approach: any laborRate value > 0 == reviewed. The backend is moving
// toward not pre-seeding labor rates, so an unconfigured team will have an
// empty/missing laborRate object; once the user saves real numbers in
// Settings → Fees, the warning + gate clear automatically.
export function feesNotReviewed(metadata: Record<string, unknown> | null | undefined): boolean {
  if (!metadata) return true
  const labor = (metadata as { laborRate?: Record<string, unknown> }).laborRate
  if (!labor) return true
  return !Object.values(labor).some((v) => typeof v === "number" && v > 0)
}

function isAtPlaceholder(
  value: number | undefined,
  defaultValue: number,
): boolean {
  return value == null || value === defaultValue
}

// Counts how many of the 12 metadata values still match placeholder. Used by
// the Settings page summary ("X of 12 still placeholder").
export function countPlaceholderFields(
  metadata: Record<string, unknown> | null | undefined,
): { placeholderCount: number; total: number } {
  const total = 12
  if (!metadata) return { placeholderCount: total, total }

  let placeholder = 0
  const sections: Array<[keyof typeof BODYSHOP_PLACEHOLDER_METADATA, Record<string, number>]> = [
    ["laborRate", BODYSHOP_PLACEHOLDER_METADATA.laborRate],
    ["materials", BODYSHOP_PLACEHOLDER_METADATA.materials],
    ["extraFees", BODYSHOP_PLACEHOLDER_METADATA.extraFees],
    ["vehicleScans", BODYSHOP_PLACEHOLDER_METADATA.vehicleScans],
  ]
  for (const [key, defaults] of sections) {
    const section = (metadata as Record<string, Record<string, number> | undefined>)[key] ?? {}
    for (const [field, defaultValue] of Object.entries(defaults)) {
      if (isAtPlaceholder(section[field], defaultValue)) placeholder++
    }
  }
  return { placeholderCount: placeholder, total }
}
